import {DeploymentAccount, PipelineStage} from '../types';

// FNP-1017: the deploy ladder. Design doc §7 — a console at a given tier is
// reached by climbing every rung below it:
//   dev console  -> DeployDev
//   test console -> DeployDev -> DeployTest
//   prod console -> DeployDev -> DeployTest -> DeployProd
// The admin must SEE all three rungs. A rung that no deployment account can
// satisfy renders DISABLED rather than hidden — the whole point of this screen
// is that every option is observable, so an admin can tell "not offered" apart
// from "not applicable to this account group".
export type LadderTier = 'DEV' | 'TEST' | 'PROD';

export const LADDER_TIERS: readonly LadderTier[] = ['DEV', 'TEST', 'PROD'] as const;

export const ladderStageName = (tier: LadderTier): string =>
    `Deploy${tier.charAt(0)}${tier.slice(1).toLowerCase()}`;

// StageKind is the field the backend CANNOT infer. pipeline-stage-validator.ts
// throws when a non-Source stage carries neither stageKind nor deployProvider,
// because pipeline-cft-builder buckets stages by stageKind and SILENTLY DROPS
// anything unclassified — producing a green pipeline with missing actions.
export const STAGE_KIND_OPTIONS = [
    {value: 'UNIT_TEST', label: 'Unit Test — runs the test suite'},
    {value: 'BUILD', label: 'Build — compiles / synthesizes artifacts'},
    {value: 'ASSETS', label: 'Assets — publishes CDK assets'},
    {value: 'DEPLOY', label: 'Deploy — applies a stack to a target account'},
];

export const STAGE_KINDS: readonly string[] = STAGE_KIND_OPTIONS.map((o) => o.value);

// Mirrors pipeline-stage-validator.ts:inferStageNameFromAccountName. An account
// classifies to a tier by the suffix of its NAME. Kept in sync deliberately: the
// UI must offer the same pairing the backend will compute, or the admin selects a
// target that provisioning then resolves differently.
// ⚠️ FNP-1242: SUFFIX ONLY. The `includes()` fallbacks that used to sit beside
// each `endsWith()` are gone, and they were not a harmless widening.
//
// The checks run dev, then test, then prod. `flexi-testing-prod` hit the SECOND
// one — "testing" contains "test" — and classified as TEST. So did
// `flexi-testing-test`, and `flexi-testing-dev` matched `includes('dev')` first
// by luck. All three Testing OU accounts collapsed into one tier, no account
// classified as PROD, and provisioning reported the production account missing
// while Organizations was returning all three ACTIVE.
//
// It surfaced only when a fifth OU appeared, because only "testing" contains
// another tier's name. billing, marketplace, domains, email and shared-svcs all
// happened to be safe under substring matching.
//
// The suffix is the contract — inferStageNameFromAccountName in the API's
// pipeline-stage-validator.ts matches the same way, and this must agree with it
// or the UI offers a pairing the backend then computes differently.
export const accountTier = (account?: DeploymentAccount): LadderTier | undefined => {
    const name = (account?.accountName || '').toLowerCase();
    if (name.endsWith('-dev')) return 'DEV';
    if (name.endsWith('-test')) return 'TEST';
    if (name.endsWith('-prod')) return 'PROD';
    return undefined;
};

// FNP-1017: which ladder rungs the CURRENT account selection can actually
// satisfy. A tier with no matching account is still rendered — just disabled.
export const availableTiers = (accounts?: DeploymentAccount[]): Set<LadderTier> => {
    const tiers = new Set<LadderTier>();
    (accounts ?? []).forEach((a) => {
        const tier = accountTier(a);
        if (tier) tiers.add(tier);
    });
    return tiers;
};

// FNP-1024: THE CONSOLE'S OWN STAGE CAPS THE LADDER.
//
// Design doc §7: the console determines the AMBITION. A test console builds
// DeployDev -> DeployTest and stops; only the prod console may build DeployProd.
//
// This gate was missing entirely. `availableTiers` asks only "does the selected
// account group contain an account of this tier?" — and Shared Services,
// Billing, Marketplace and Flexi Domains OUs all contain a prod account. So the
// prod rung was clickable from EVERY console, and the fieldset legend directly
// above it already claimed otherwise. Documentation without a gate.
//
// ⚠️ This is the same category error as FNP-1014: the console's stage is a
// build-time constant the app already holds (`config/stage.ts`), and the code
// simply never consulted it.
//
// `ide` maps to `dev`, matching applications/pages/EditPage.tsx:59 — without it
// local development silently loses every rung.
export type ConsoleStage = 'ide' | 'dev' | 'test' | 'prod';

const TIER_RANK: Record<LadderTier, number> = {DEV: 0, TEST: 1, PROD: 2};

const CONSOLE_TIER: Record<Exclude<ConsoleStage, 'ide'>, LadderTier> = {
    dev: 'DEV',
    test: 'TEST',
    prod: 'PROD',
};

// The highest rung this console may build. An unrecognised stage is treated as
// `dev` — the LEAST capable tier — so a misconfigured VITE_STAGE fails closed
// rather than unlocking prod.
export const consoleCeilingTier = (consoleStage?: string): LadderTier => {
    const normalized = consoleStage === 'ide' ? 'dev' : consoleStage;
    return CONSOLE_TIER[normalized as Exclude<ConsoleStage, 'ide'>] ?? 'DEV';
};

export const isTierWithinConsole = (tier: LadderTier, consoleStage?: string): boolean =>
    TIER_RANK[tier] <= TIER_RANK[consoleCeilingTier(consoleStage)];

// Why a rung cannot be built, or undefined when it can. The two reasons are
// deliberately distinct: "this OU has no such account" is fixable by picking a
// different group, "this console cannot reach that tier" is not fixable here at
// all. Collapsing them into one message sends the admin hunting for the wrong
// thing.
export const ladderRungBlockedReason = (
    tier: LadderTier,
    tiersWithAccounts: Set<LadderTier>,
    consoleStage?: string,
): string | undefined => {
    if (!isTierWithinConsole(tier, consoleStage)) {
        return `${ladderStageName(tier)} is provisioned from the `
            + `${tier.toLowerCase()} console. This console builds up to `
            + `${ladderStageName(consoleCeilingTier(consoleStage))}.`;
    }
    if (!tiersWithAccounts.has(tier)) {
        return `No ${tier} account in the selected deployment account group - `
            + 'this rung cannot be built.';
    }
    return undefined;
};

// FNP-1149: match on TIER, not on the raw name.
//
// This compared `s.name === ladderStageName(tier)` — exact, case-sensitive
// equality against the ladder's own spelling. But the drawer seeds the form from
// `analysis.suggestedStages`, and the backend analyzer names the same rungs
// LOWERCASE: `test`/`prod` (archetype-defaults.ts), not `DeployTest`/`DeployProd`.
// So `'test' === 'DeployTest'` was false and the TEST checkbox rendered UNTICKED
// while a test deploy stage sat two rows above it in the list.
//
// That unchecked box is not cosmetic. It tells the admin a deploy stage is
// missing when it is not, and ticking it runs an unconditional push — so acting
// on the false signal produced `test` AND `DeployTest`, two CloudFormation
// deploys of one stack into one account. `stageTier` already normalizes both
// spellings to the same tier; this is the read half using it.
export const isLadderStagePresent = (stages: PipelineStage[] | undefined, tier: LadderTier): boolean =>
    (stages ?? []).some((s) => isDeployStageForTier(s, tier));

// FNP-1149: the index of the DEPLOY stage occupying a rung, or -1. The uncheck
// path needs this for the same reason the check path needs isLadderStagePresent
// — it looked the stage up by the ladder's spelling, so unticking a box that had
// been ticked against an analyzer-named stage removed nothing.
export const findLadderStageIndex = (stages: PipelineStage[] | undefined, tier: LadderTier): number =>
    (stages ?? []).findIndex((s) => isDeployStageForTier(s, tier));

// Only a DEPLOY stage occupies a ladder rung. Guarding on kind keeps a UNIT_TEST
// stage named `UnitTest` from claiming the TEST rung — `stageTier` matches on a
// loose substring, so without this the analyzer's own `UnitTest` would read as a
// test deploy. Legacy records carry deployProvider without stageKind, matching
// how pipeline-cft-builder and pipeline-stage-validator classify them.
const isDeployStageForTier = (stage: PipelineStage | undefined, tier: LadderTier): boolean => {
    if (!stage || isSourceStage(stage)) return false;
    const isDeploy = stage.stageKind === 'DEPLOY' || (!stage.stageKind && !!stage.deployProvider);
    return isDeploy && stageTier(stage) === tier;
};

// A DEPLOY stage with no stageKind is exactly the shape the CFT builder drops.
// Surfaced in the form so the admin cannot submit it — see validateStages below.
export const isSourceStage = (stage?: PipelineStage): boolean => stage?.name === 'Source';

export const stageNeedsKind = (stage?: PipelineStage): boolean =>
    !!stage && !isSourceStage(stage) && !stage.stageKind;

export type StageValidationError = {
    index: number,
    name: string,
    message: string,
};

// FNP-1017 client-side mirror of pipelines-api validatePipelineStages. This does
// NOT replace the server guard — it exists so the admin gets the error at the
// field instead of as an opaque provisioning failure. The server remains
// authoritative; see the prod-approval note in ProvisionPipelineDrawer.
// FNP-1346: `accounts` is optional so existing callers keep working. Pass it to
// get the deploy-target check, which cannot be made without knowing which accounts
// are selectable.
export const validateStages = (
    stages?: PipelineStage[],
    accounts?: DeploymentAccount[],
): StageValidationError[] => {
    const errors: StageValidationError[] = [];
    const list = stages ?? [];

    if (list.length === 0) {
        return [{index: -1, name: '', message: 'A pipeline must have at least one stage.'}];
    }

    list.forEach((stage, index) => {
        if (!stage?.name?.trim()) {
            errors.push({index, name: stage?.name ?? '', message: 'Stage name is required.'});
        }
        // stageKind is MANDATORY on every non-Source stage.
        if (stageNeedsKind(stage)) {
            errors.push({
                index,
                name: stage.name,
                message: 'Stage kind is required — an unclassified stage is silently dropped from the generated pipeline.',
            });
        }
        // FNP-1345/1346: a DEPLOY stage must name where it lands. The API throws
        // rather than defaulting one, so catching it here turns an opaque
        // provisioning failure into a field the admin can see and fix.
        if (accounts && stage?.stageKind === 'DEPLOY' && !resolveDeployAccount(stage, accounts)) {
            errors.push({
                index,
                name: stage.name,
                message: 'Deploy target account is required — a deploy stage with no target is refused at provision time.',
            });
        }
    });

    return errors;
};

// Renumber `order` to match array position after add / remove / reorder. `order`
// is what the backend sorts by, so a stale value after a swap reorders the
// generated pipeline in a way the admin did not see on screen.
export const renumberStages = (stages: PipelineStage[]): PipelineStage[] =>
    stages.map((stage, index) => ({...stage, order: index + 1}));

// Which deployment account a DEPLOY stage will ACTUALLY land in.
//
// FNP-1346 added `deployTargetAccountId` to `PipelineStageInput`, so the target is
// now CARRIED BY THE STAGE. A stored id wins outright: it is what the admin picked
// and what the backend will use.
//
// ⚠️ The name-inference branch below is a SEED for records written before that
// field existed, not a fallback for new ones. It reads 'dev'/'test'/'prod' out of
// the stage name and matches an account of the same tier — which is why a tools
// deploy was previously impossible to express: `flexi-tools` carries no such
// suffix, so nothing ever resolved and the stage had no target at all.
//
// It must not become load-bearing again. The API no longer defaults a deploy
// target and throws when one is missing (FNP-1345), so a stage this function
// cannot resolve is an error the admin has to fix, not a shape the backend will
// paper over. See docs/decisions/pipeline-structure-model.md.
export const resolveDeployAccount = (
    stage: PipelineStage | undefined,
    accounts: DeploymentAccount[] | undefined,
): DeploymentAccount | undefined => {
    const explicit = stage?.deployTargetAccountId;
    if (explicit) {
        return (accounts ?? []).find((a) => a.accountId === explicit);
    }
    const tier = stageTier(stage);
    if (!tier) return undefined;
    return (accounts ?? []).find((a) => accountTier(a) === tier);
};

// The tier a stage belongs to, from its NAME — mirroring the backend's
// stageEnvOf/inferStageNameFromAccountName pairing (FNP-1002).
export const stageTier = (stage?: PipelineStage): LadderTier | undefined => {
    const name = (stage?.name || '').toLowerCase();
    if (!name) return undefined;
    if (name.includes('dev')) return 'DEV';
    if (name.includes('test')) return 'TEST';
    if (name.includes('prod')) return 'PROD';
    return undefined;
};

export const makeLadderStage = (tier: LadderTier, order: number): PipelineStage => ({
    name: ladderStageName(tier),
    order,
    stageKind: 'DEPLOY',
    deployProvider: 'CLOUDFORMATION',
    // PROD rungs default to gated. FNP-709 enforces this server-side regardless;
    // defaulting it true here means the admin has to make an explicit, visible
    // choice to remove a gate rather than silently omitting one.
    requiresApproval: tier === 'PROD',
});

export const makeCustomStage = (order: number): PipelineStage => ({
    name: '',
    order,
    // Deliberately NO stageKind — the admin must choose it. See validateStages.
    stageKind: undefined,
    requiresApproval: false,
});
